import React, { useCallback, useState, useEffect } from 'react';
import {
  Checkbox,
  Box,
  Button,
  IconButton,
  Slider,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LayersIcon from '@mui/icons-material/Layers';
import CloseIcon from '@mui/icons-material/Close';
import OpacityIcon from '@mui/icons-material/Opacity';
import {
  DragDropContext,
  Droppable,
  Draggable,
} from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration';

import { Legend } from './Legend';

export function Layers({ layers, canOpen = true, triggerControlChange }) {
  const {
    toggle: toggleLayer,
    isLoading,
    isHidden,
    isDisabled,
    isVisible,
    updateStyle,
    updateZIndex,
  } = layers;

  const [order, setOrder] = useState(layers.map((l) => l.id));
  const [isOpen, setOpen] = useState();
  const [visibleLegends, setVisibleLegends] = useState([]);
  const [visibleOpacities, setVisibleOpacities] = useState([]);

  const onDragEnd = useCallback(({ destination, source }) => {
    if (destination && source) {
      setOrder((current) => {
        const layerId = current[source.index];
        current.splice(source.index, 1);
        current.splice(destination.index, 0, layerId);
        return [...current];
      });
    }
  }, []);

  useEffect(() => {
    layers
      .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
      .forEach((layer, index) => updateZIndex(layer, index));
  }, [layers, order, updateZIndex]);

  useEffect(() => {
    if (!canOpen) setOpen(false);
  }, [canOpen]);

  return (
    <Box
      sx={{
        m: 1,
        position: 'relative',
      }}
    >
      <Button
        title="Layer List"
        variant="contained"
        color="inherit"
        onClick={handleButtonClick}
        sx={{
          pointerEvents: 'auto',
          minWidth: 0,
          width: 36,
          bgcolor: 'white',
          mb: 1,
        }}
      >
        <LayersIcon
          fontSize="medium"
          sx={{
            color: isOpen ? 'black' : '#666',
            '&:hover': { color: '#333' },
          }}
        />
      </Button>
      <Box
        className="layer-list"
        sx={{
          display: isOpen ? 'flex' : 'none',
          flexDirection: 'column',
          minWidth: 350,
          bgcolor: 'white',
          pointerEvents: 'auto',
          position: 'absolute',
          right: 0,
          maxHeight: 'calc(100% - 60px)',
        }}
      >
        {buildHeader()}
        {buildLayersContainer()}
      </Box>
    </Box>
  );

  function buildHeader() {
    return (
      <Box
        sx={{
          fontSize: 15,
          bgcolor: 'black',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box sx={{ flex: 1, pl: 1.5 }}>Map Layers</Box>
        <IconButton title="Close" onClick={() => setOpen(!isOpen)}>
          <CloseIcon sx={{ color: 'white' }} />
        </IconButton>
      </Box>
    );
  }

  function buildLayersContainer() {
    return (
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provider) => (
              <div {...provider.droppableProps} ref={provider.innerRef}>
                {buildLayers()}
                {provider.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    );
  }

  function buildLayers() {
    return layers
      .filter((layer) => !isHidden(layer))
      .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
      .map((layer, index) => (
        <Draggable key={layer.id} draggableId={layer.id} index={index}>
          {(provider) => (
            <div
              ref={provider.innerRef}
              {...provider.draggableProps}
              {...provider.dragHandleProps}
            >
              {buildLayer(layer, index, provider)}
            </div>
          )}
        </Draggable>
      ));
  }

  function buildLayer(layer, i, provider) {
    const isLayerVisible = isVisible(layer);
    const isLegendVisible = visibleLegends.includes(layer.id);

    return (
      <Box key={i} data-testid="map-layer">
        <Box
          sx={{ fontSize: 15, display: 'flex', alignItems: 'center', py: 0 }}
        >
          {buildLayerCheckbox(layer)}
          {buildActions(layer, provider)}
        </Box>

        {isLayerVisible && isLegendVisible && <Legend layer={layer} />}
      </Box>
    );
  }

  function buildLayerCheckbox(layer) {
    const isLayerVisible = isVisible(layer);
    const isLayerLoading = isLoading(layer);
    const isLayerDisabled = isDisabled(layer);
    const showSpinner = isLayerLoading;

    const getCheckboxColor = () => {
      if (layer.error) return 'error';
      if (isLayerDisabled) return 'primary';
      return 'default';
    };

    const checkbox = showSpinner ? (
      <CircularProgress color="inherit" sx={{ m: 1.5 }} size={18} />
    ) : (
      <Checkbox
        color={getCheckboxColor()}
        inputProps={{ 'data-testid': `layer-${layer.id}-visibility` }}
        checked={isLayerVisible}
        onChange={() => toggleLayer(layer)}
      />
    );

    return (
      <FormControlLabel
        disabled={isLayerDisabled}
        sx={{ flex: 1, pl: 1.5, pr: 2.5, cursor: 'grab' }}
        control={checkbox}
        label={buildLayerName(layer)}
      />
    );
  }

  function buildLayerName(layer) {
    const builder = layer.buildLayerLabel || (() => layer.name);
    return isVisible(layer) ? builder(layer) : layer.name;
  }

  function buildOpacityAction(layer, dragProvider) {
    if (layer.opacityControl && isVisible(layer) && !isDisabled(layer)) {
      return (
        <Box style={{ position: 'relative' }}>
          <IconButton
            title="Opacity"
            sx={{ py: 0, position: 'absolute', right: 0 }}
            onClick={() => toggleOpacity(layer)}
          >
            <OpacityIcon />
          </IconButton>
          {visibleOpacities.includes(layer.id) && (
            <Box
              data-rbd-drag-handle-context-id={
                dragProvider.dragHandleProps?.[
                  'data-rbd-drag-handle-context-id'
                ]
              }
              data-rbd-drag-handle-draggable-id="ignore-drag-and-drop"
              sx={{
                cursor: 'auto',
                position: 'absolute',
                bgcolor: 'white',
                right: 0,
                width: 100,
                pr: '16px',
              }}
            >
              <Slider
                color="primary"
                defaultValue={layer.dataLayer.style.fillOpacity * 100}
                onChange={(event) => changeOpacity(layer, event.target.value)}
                onChangeCommitted={() => toggleOpacity(layer)}
              />
            </Box>
          )}
        </Box>
      );
    }
  }

  function buildLegendAction(layer) {
    if (layer.legend && isVisible(layer) && !isDisabled(layer)) {
      return (
        <IconButton title="Legend" onClick={() => toggleLegend(layer)}>
          <FormatListBulletedIcon />
        </IconButton>
      );
    }
  }

  function buildActions(layer, dragProvider) {
    return (
      <Box
        sx={{ minWidth: 34, display: 'flex', '& button': { py: 0, px: 0.5 } }}
      >
        {buildOpacityAction(layer, dragProvider)}
        {buildLegendAction(layer)}
      </Box>
    );
  }

  function toggleLegend(layer) {
    setVisibleLegends(toggle(layer));
  }

  function toggleOpacity(layer) {
    setVisibleOpacities(toggle(layer));
  }

  function toggle(layer) {
    return (currentState) => {
      if (!currentState.includes(layer.id)) return [...currentState, layer.id];
      return currentState.filter((k) => k !== layer.id);
    };
  }

  function changeOpacity(layer, value) {
    updateStyle(layer, { fillOpacity: value / 100 });
  }

  function handleButtonClick() {
    const shouldOpen = !isOpen;
    if (shouldOpen && triggerControlChange) triggerControlChange();
    setOpen(shouldOpen);
  }
}
